// "What our participants say" — the training's feedback videos, between the
// outcomes and the FAQ on both landing templates. Renders nothing when the
// training has none, so a training without videos looks exactly as before.

import { ExternalLink, Play } from "lucide-react";
import { ShortsPlayer } from "#/components/shorts-player";
import { Reveal } from "#/components/ui";
import {
	type FeedbackVideo,
	feedbackVideoPlayback,
} from "#/lib/feedback-videos";
import { type Locale, useI18n } from "#/lib/i18n";

// The content JSON has no key for this section, and every training would need
// one added by hand — so its few strings live here, per locale.
const COPY: Record<
	Locale,
	{
		eyebrow: string;
		heading: string;
		sub: string;
		play: string;
		watch: string;
		unmute: string;
	}
> = {
	ar: {
		eyebrow: "آراء المتدربين",
		heading: "ماذا يقول من حضروا التكوين",
		sub: "شهادات مصوّرة من مشاركين في الطبعات السابقة.",
		play: "تشغيل الفيديو",
		watch: "مشاهدة الفيديو",
		unmute: "تشغيل الصوت",
	},
	fr: {
		eyebrow: "Témoignages",
		heading: "Ce qu'en disent nos participants",
		sub: "Des retours filmés de participants des sessions précédentes.",
		play: "Lire la vidéo",
		watch: "Voir la vidéo",
		unmute: "Activer le son",
	},
	en: {
		eyebrow: "Testimonials",
		heading: "What our participants say",
		sub: "Filmed feedback from participants in previous sessions.",
		play: "Play video",
		watch: "Watch the video",
		unmute: "Unmute",
	},
};

export function FeedbackVideosSection({
	videos,
	fallbackPoster,
}: {
	videos: FeedbackVideo[];
	/** Shown when a YouTube video serves no thumbnail (unlisted ones don't). */
	fallbackPoster: string;
}) {
	const { locale } = useI18n();
	if (!videos.length) return null;
	const t = COPY[locale];

	return (
		<section
			id="feedback"
			data-section="feedback"
			className="scroll-mt-20 bg-white py-16 sm:py-20 lg:py-24"
		>
			<div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
				<Reveal className="mb-10 flex flex-col items-start gap-4">
					<span className="text-xs font-bold uppercase tracking-[0.18em] text-gold-600">
						{t.eyebrow}
					</span>
					<h2 className="font-display text-3xl font-bold leading-tight text-navy sm:text-4xl">
						{t.heading}
					</h2>
					<p className="text-base leading-relaxed text-muted">{t.sub}</p>
				</Reveal>

				{/* A single video stays a comfortable width instead of stretching. */}
				<div
					className={`grid gap-5 ${
						videos.length === 1
							? "mx-auto max-w-xs"
							: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
					}`}
				>
					{videos.map((video) => (
						<Reveal key={video.id} className="flex flex-col gap-2.5">
							<FeedbackVideoPlayer
								video={video}
								fallbackPoster={fallbackPoster}
								copy={t}
							/>
							{video.title ? (
								<p className="text-sm font-semibold leading-snug text-navy">
									{video.title}
								</p>
							) : null}
						</Reveal>
					))}
				</div>
			</div>
		</section>
	);
}

function FeedbackVideoPlayer({
	video,
	fallbackPoster,
	copy,
}: {
	video: FeedbackVideo;
	fallbackPoster: string;
	copy: (typeof COPY)[Locale];
}) {
	const playback = feedbackVideoPlayback(video);
	const label = video.title || copy.play;

	if (playback.kind === "youtube") {
		return (
			<ShortsPlayer
				videoId={playback.videoId}
				poster={null}
				fallbackPoster={fallbackPoster}
				// The title is already printed under the card; on the poster it
				// would just repeat.
				label={copy.play}
				hint=""
				section="feedback"
				unmuteLabel={copy.unmute}
			/>
		);
	}

	if (playback.kind === "file") {
		return (
			// `preload="metadata"` fetches only enough to show the first frame and
			// the duration — the page doesn't download every testimonial upfront.
			// object-contain keeps landscape clips whole inside the 9:16 frame.
			// biome-ignore lint/a11y/useMediaCaption: spoken testimonials, no caption files exist
			<video
				src={playback.src}
				controls
				playsInline
				preload="metadata"
				aria-label={label}
				className="aspect-9/16 w-full rounded-[2rem] bg-navy-900 object-contain shadow-lift ring-1 ring-line"
			/>
		);
	}

	return (
		<a
			href={playback.href}
			target="_blank"
			rel="noopener noreferrer"
			data-track="feedback_video_external"
			data-track-type="video"
			data-track-section="feedback"
			className="group relative flex aspect-9/16 w-full flex-col items-center justify-center gap-3 overflow-hidden rounded-[2rem] bg-navy-900 p-4 text-center text-white shadow-lift ring-1 ring-line"
		>
			<span className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-navy shadow-lift transition-transform duration-300 group-hover:scale-110">
				<Play className="h-6 w-6 translate-x-0.5 fill-current" />
			</span>
			<span className="inline-flex items-center gap-1.5 text-sm font-bold">
				{copy.watch}
				<ExternalLink className="h-3.5 w-3.5" />
			</span>
		</a>
	);
}
